import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { PropertyInfo } from '../appraisal-container/appraisal-container.component';

import { AgGridModule } from 'ag-grid-angular';
import {
  type ColDef, type GetRowIdFunc, type GetRowIdParams, type GridReadyEvent, type ValueFormatterFunc,
  type ValueGetterParams, FirstDataRenderedEvent, GridApi, GridOptions, LicenseManager, SideBarDef
  , GridChartsModule, MenuModule, RowGroupingModule, AdvancedFilterModule , ColumnsToolPanelModule , ExcelExportModule 
  , FiltersToolPanelModule , RangeSelectionModule , RichSelectModule , SetFilterModule , SparklinesModule , StatusBarModule, 
  GetDetailRowDataParams,
  SizeColumnsToFitGridStrategy
} from 'ag-grid-enterprise';
import 'ag-grid-enterprise';

import { ContainerActionsComponent } from "../container-actions/container-actions.component";
import { ApprovalStatusRenderer } from '../../shared/components/ApprovalStatusRenderer';
import { getData } from './data';
import { ActionsCellRenderer } from './cell-renderer/actions-cell-renderer.component';

const statuses = {
  all: 'All',
  active: 'Active',
  paused: 'On  Hold',
  outOfStock: 'Out of Stock',
};

@Component({
  selector: 'app-appraisal-table',
  standalone: true,
  templateUrl: './appraisal-table.component.html',
  styleUrl: './appraisal-table.component.scss',
  imports: [CommonModule, AgGridModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatButtonModule, MatIconModule, ContainerActionsComponent]
})
export class AppraisalTableComponent {

  constructor(private http: HttpClient) {
    LicenseManager.setLicenseKey("Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-060398}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Cushman_&_Wakefield_Global,_Inc}_is_granted_a_{Multiple_Applications}_Developer_License_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Charts_and_AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{18_June_2025}____[v3]_[0102]_MTc1MDIwMTIwMDAwMA==e09d5ef40227227bc0761c5be7738da7");
  }
  
  @Input() gridTheme: string = 'ag-theme-quartz';
  @Input() isDarkMode: boolean = false;

  getRowId: any = (params: GetRowIdParams) => params.data.ticker;
  enableCharts = true;
  enableRangeSelection = true;
  rowSelection: 'multiple' | 'single' | undefined = 'multiple';
  rowGroupPanelShow: 'always' | 'onlyWhenGrouping' | 'never' | undefined = 'always';
  suppressAggFuncInHeader: boolean = true;
  groupDefaultExpanded = 0;
  sidebar: SideBarDef = {
    toolPanels: [
      'columns',
      'filters',
    ]
  };
  private gridApi!: GridApi;

  themeClass = `${this.gridTheme}${this.isDarkMode ? '-dark' : ''}`;

  rowData = getData();
  columnDefs : any = [{
      field: 'jobCenterId',
      headerName: 'Job Center ID',
      cellRenderer: 'agGroupCellRenderer',
      headerClass: 'header-product',
      minWidth: 150,
    },
    { field: "appraiserName", headerName: 'Appraiser Name', filter: "agTextColumnFilter", enableRowGroup: true, enablePivot: true, chartDataType: 'category' },
    { field: "propertyType", headerName: 'Property Type', filter: "agTextColumnFilter", enableRowGroup: true, enablePivot: true, minWidth: 125, aggFunc: 'sum', chartDataType: 'series' },
    { field: 'address', headerName: 'Address', filter: true, enableRowGroup: true, enablePivot: true },
    { field: 'trend', headerName: 'Trend', filter: true, cellRenderer: 'agSparklineCellRenderer', chartDataType: 'series' },
    {
      field: 'approvalStatus', headerName: 'Approval Status', filter: true,  chartDataType: 'series', aggFunc: 'sum', cellEditor: "agRichSelectCellEditor", cellEditorParams: {
        // cellRenderer: ApprovalStatusRenderer, values: ["A", "R", "N"],
      }
    },
    { field: 'city', headerName: 'City', filter: true },
    { field: 'st', headerName: 'State', filter: true },
    { field: 'propertyName', headerName: 'Property Name', filter: true },
    { field: 'totalLandArea', headerName: 'Total Land Area', filter: true, chartDataType: 'series', cellEditor: "agNumberCellEditor" },
    { field: 'buildingSF', headerName: 'Building SF', filter: true, chartDataType: 'series', cellEditor: "agNumberCellEditor" },
    { field: 'yearBuilt', headerName: 'Year Built', filter: true, chartDataType: 'series', cellEditor: "agNumberCellEditor" },
    { field: 'yearRenovated', headerName: 'Year Renovated', filter: true, chartDataType: 'series' },
    { field: 'currentOccupancy', headerName: 'Current Occupancy', filter: true, chartDataType: 'series' },
    { field: 'majorTenants', headerName: 'Major Tenants', filter: true },
    { field: 'coveredParking', headerName: 'Covered Parking', filter: true },
    { field: 'openParking', headerName: 'Open Parking', filter: 'agNumberColumnFilter' },
    { field: 'noOfDockHighDoors', headerName: 'No. of Dock High Doors', filter: true },
    { field: 'office', headerName: 'Office', filter: true },
    { field: 'clearHeight', headerName: 'Clear Height', filter: true, cellEditor: "agNumberCellEditor" },
    { field: 'buildingClass', headerName: 'Building Class', filter: true },
    // { field: 'actions', cellRenderer: ActionsCellRenderer, minWidth: 194 },
  ];
  defaultColDef: any = {
    resizable: true,
    filter: true,
    floatingFilter: true,
    editable: true,
    enableRowGroup: true
  };
  autoSizeStrategy: SizeColumnsToFitGridStrategy = {
    type: 'fitGridWidth',
  };
  detailCellRendererParams = {
    detailGridOptions: {
      columnDefs: [
        { field: 'title', flex: 1.5 },
        { field: 'available', maxWidth: 120 },
        { field: 'format', flex: 2 },
        { field: 'label', flex: 1 },
        { field: 'country', flex: 0.66 },
        {
          field: 'cat',
          headerName: 'Cat#',
          type: 'rightAligned',
          flex: 0.66,
        },
        { field: 'year', type: 'rightAligned', maxWidth: 80 },
      ],
      headerHeight: 38,
    },
    getDetailRowData: ({
      successCallback,
      data: { variantDetails },
    }: GetDetailRowDataParams) => successCallback(variantDetails),
  };
  rowHeight = 60;
  paginationPageSizeSelector = [5, 10, 20];
  pagination = true;
  paginationPageSize = 10;
  masterDetail = true;
  detailRowAutoHeight = true;

  onGridReady(params: any) {
    this.gridApi = params.api;
  }

  statusEntries = Object.entries(statuses);
  activeTab = 'all';
  quickFilterText = '';

  handleTabClick(status: string) {
    this.gridApi.setColumnFilterModel(
      'status',
      status === 'all' ? null : { values: [status] },
    );
    this.gridApi.onFilterChanged();
    this.activeTab = status;
  }
}
